import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Row, Col, Offcanvas, OffcanvasBody,
  Nav, NavItem,
} from 'reactstrap';

import { IRootState } from '@app/store';
import navMenus from '@config/navbar';
import LocaleService from '@services/LocaleService';
import { CustomButton } from '@shared/buttons';
import NotificationDot from '@shared/components/notification-dot/NotificationDot';
import useNotificationChecker from '@shared/hooks/useNotificationStatus';
import { CloseIcon } from '@shared/icons';

import './DashboardNavDrawer.scss';

type DashboardNavDrawerProps = {
  isOpen: boolean;
  toggleDrawer: (e:any) => void;
};

// Define a type for user type list
type UserTypeList = {
  dentist: string;
  'dental-clinic': string;
  laboratory: string;
};

// Define UserType with a union of keys of UserTypeList
type UserType = keyof UserTypeList;

const DashboardNavDrawer = ({ isOpen, toggleDrawer }: DashboardNavDrawerProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const userType = useSelector((state: IRootState) => state.Auth?.user?.type) as UserType;
  // const [ activeLink, setActiveLink ] = useState(location.pathname);
  // console.log('activeLinkValue 2', activeLink);
  const i18n = LocaleService.getTranslations('navigation');

  const hasNotification = useNotificationChecker();

  const onClickNav = (event: any, navTo: any) => {
    // setActiveLink(navTo);
    navigate(navTo);
    toggleDrawer(event);
  };

  const onSendReferral = (event: any) => {
    navigate('/send/referral', {
      state: {
        // Keep existing state
        ...location?.state,
        // Add returnTo property
        returnTo: location?.pathname,
      },
    });
    // setActiveLink('/send/referral');
    toggleDrawer(event);
  };

  const onSendLabOrder = (event: any) => {
    navigate('/send/lab-orders', {
      state: {
      // Keep existing state
        ...location?.state,
        // Add returnTo property
        returnTo: location?.pathname,
      },
    });
    // setActiveLink('/send/lab-orders');
    toggleDrawer(event);
  };

  return (
    <Offcanvas direction="end" isOpen={isOpen} toggle={toggleDrawer}>
      <OffcanvasBody className="dashboard-nav-canvas-body">
        <Row className="nav-drawer-header-style">
          <Col>
            <a
              href="#"
              onClick={toggleDrawer}
              className="mb-3"
            >
              <CloseIcon />
            </a>
          </Col>
        </Row>
        <div className="nav-drawer-border-style" />
        <Nav vertical>
          {navMenus && userType && navMenus[userType].map((nav: any) => (
            <NavItem
              key={nav.title}
              className={classNames('nav-drawer-item', {
                'nav-drawer-item-active': location.pathname === nav.to || (location.pathname.startsWith(nav.key) && nav.to !== '/'),
              })}
              onClick={(e) => onClickNav(e, nav.to)}
            >
              <CustomButton
                className={classNames('nav-drawer-item-btn', {
                  'nav-drawer-item-btn-active': location.pathname === nav.to || (location.pathname.startsWith(nav.key) && nav.to !== '/'),
                })}
                color="link"
              >
                <div className="d-flex align-items-center gap-2">
                  <span>{nav.title}</span>
                  {hasNotification(nav.key) && <NotificationDot />}
                </div>
              </CustomButton>

            </NavItem>
          ))}
        </Nav>
        <div className="nav-drawer-border-style mb-4" />
        <Row className="custom-buttons-row-style">
          <Col>
            <CustomButton
              id="send-referral-via-dashboard-drawer"
              className="btn-lg send-button-style"
              color="primary"
              onClick={onSendReferral}
            >
              {i18n.button.send_referral}
            </CustomButton>
          </Col>
        </Row>
        <Row className="custom-buttons-row-style">
          <Col>
            <CustomButton
              id="send-lab-order-via-dashboard-drawer"
              className="btn-lg send-button-style"
              color="primary"
              onClick={onSendLabOrder}
            >
              {i18n.button.send_lab_order}
            </CustomButton>
          </Col>
        </Row>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default DashboardNavDrawer;
